import { Injectable } from '@angular/core';
import { WebAuthService } from '../auth/services/web-auth.service';
import { IWebAuthData } from '../shared/models';
import { take } from 'rxjs/operators';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class CrossDomainStateService {
  private readonly WEBAUTH_QUERY_PARAM = 'webauth_state';
  private readonly CHECKOUT_QUERY_PARAM = 'checkout_state';
  // Order keys we accept back from the redirect param; anything else a crafted link injects is dropped.
  private readonly CHECKOUT_STATE_KEYS = [
    'item_id',
    'coupon_id',
    'country',
    'region',
    'entity',
    'period_unit',
    'term',
    'qty',
  ];

  // Checkout order context (item/term/qty/coupon) staged for the next region redirect, so the
  // single-page checkout resumes on the account's region host.
  private stagedCheckoutState: Record<string, string> | null = null;

  constructor(
    private webAuthService: WebAuthService,
    private logger: LoggerService,
  ) {}

  /**
   * Stage checkout order context to be carried across the next cross-domain (region) redirect.
   * Pass null to clear.
   */
  public stageCheckoutState(state: Record<string, string> | null): void {
    this.stagedCheckoutState = state;
  }

  /**
   * Preserves all necessary states by adding them to the URL as query parameters
   * @param url The URL to append state parameters to
   * @param callback Function to call after states are preserved (e.g., to trigger redirect)
   */
  public preserveStates(url: URL, callback: () => void): void {
    const checkoutState = this.encodeJson(this.stagedCheckoutState);
    if (checkoutState) {
      url.searchParams.set(this.CHECKOUT_QUERY_PARAM, checkoutState);
      // One-shot: clear so a later redirect from this (root-scoped) singleton can't re-append it.
      this.stagedCheckoutState = null;
    }

    this.webAuthService
      .getWebAuthState$()
      .pipe(take(1))
      .subscribe((state) => {
        const webAuthState = this.encodeWebAuthState(state?.webAuthData);
        if (webAuthState) {
          url.searchParams.set(this.WEBAUTH_QUERY_PARAM, webAuthState);
        }

        callback();
      });
  }

  /**
   * Read + strip the checkout order context that was carried across a region redirect.
   * Returns null when there is none (e.g. the first, non-redirected page load).
   */
  public consumeRestoredCheckoutState(): Record<string, string> | null {
    try {
      const raw = new URLSearchParams(window.location.search).get(this.CHECKOUT_QUERY_PARAM);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(atob(raw));
      this.cleanupQueryParam(this.CHECKOUT_QUERY_PARAM);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return null;
      }
      // Keep only known order keys with string values; ignore anything else a crafted link injects.
      const state: Record<string, string> = {};
      for (const key of this.CHECKOUT_STATE_KEYS) {
        const value = (parsed as Record<string, unknown>)[key];
        if (typeof value === 'string') {
          state[key] = value;
        }
      }
      return state;
    } catch (error) {
      this.logger.error(`Error restoring checkout state from query params: ${error}`);
      return null;
    }
  }

  private encodeJson(data: unknown): string | null {
    try {
      if (!data) {
        return null;
      }
      return btoa(JSON.stringify(data));
    } catch (error) {
      this.logger.error(`Error encoding cross-domain state: ${error}`);
      return null;
    }
  }

  public restoreStates(): void {
    this.restoreWebAuthState();
  }

  private encodeWebAuthState(webAuthData: IWebAuthData): string | null {
    try {
      if (!webAuthData) {
        return null;
      }
      return btoa(JSON.stringify(webAuthData));
    } catch (error) {
      this.logger.error(`Error encoding WebAuth state: ${error}`);
      return null;
    }
  }

  private restoreWebAuthState(): void {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const webAuthState = urlParams.get(this.WEBAUTH_QUERY_PARAM);

      if (!webAuthState) {
        return;
      }

      const webAuthData: IWebAuthData = JSON.parse(atob(webAuthState));
      this.webAuthService.saveWebAuthData(webAuthData);
      this.cleanupQueryParam(this.WEBAUTH_QUERY_PARAM);
    } catch (error) {
      this.logger.error(`Error restoring WebAuth state from query params: ${error}`);
    }
  }

  private cleanupQueryParam(paramName: string): void {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete(paramName);
      const newUrl =
        window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    } catch (error) {
      this.logger.error(`Error cleaning up query parameter: ${error}`);
    }
  }
}
